import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import { Reference } from '@atlaskit/popper';
import { Box } from '@atlaskit/primitives';
import {
  PopoverContent,
  PopoverProvider,
  PopoverTarget,
  SpotlightActions,
  SpotlightBody,
  SpotlightCard,
  SpotlightControls,
  SpotlightDismissControl,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightHeadline,
  SpotlightSecondaryAction,
  SpotlightStepCount,
  type Placement
} from '@atlaskit/spotlight';
import { messages } from '@databases/intl/messages';
import { afterNextLayout } from '@databases/utils/lib/raf';
import React, {
  createContext,
  useCallback,
  type FunctionComponent,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  type RefObject,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl-next';

import { ChangeboardingExperience } from '@canvas/changeboarding';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';

const FIELD_MENU_SPOTLIGHT_STEPS = [
  'field-header',
  'field-rename',
  'freeze-column'
] as const;

const SPOTLIGHT_CARD_WIDTH = 295;
const SPOTLIGHT_CARD_SIDE_OFFSET = 6;
const VIEWPORT_PADDING = 5;
const DATABASE_SCROLL_CONTAINER_SELECTOR =
  '[data-canvas-target="database-scrollable-inner-container"]';
const ignorePopoverDismiss = () => {};

type FieldMenuSpotlightStep = (typeof FIELD_MENU_SPOTLIGHT_STEPS)[number];
type FieldMenuOpenEvent = {
  id: number;
  source: 'drag-handle' | 'field-header';
};

type FieldMenuSpotlightTourContextValue = {
  activeStep: FieldMenuSpotlightStep;
  activeTargetStep: FieldMenuSpotlightStep;
  back: () => void;
  databaseViewportAnchorRef?: RefObject<HTMLElement | null>;
  dismiss: () => void;
  next: () => void;
  done: () => void;
  steps: ReadonlyArray<FieldMenuSpotlightStep>;
};

const FieldMenuSpotlightTourContext =
  createContext<FieldMenuSpotlightTourContextValue | null>(null);

type FieldMenuSpotlightTourProviderProps = {
  children: ReactNode;
  closeFieldMenu: () => void;
  databaseViewportAnchorRef?: RefObject<HTMLElement | null>;
  fieldMenuOpenEvent?: FieldMenuOpenEvent;
  isEnabled: boolean;
  isFreezeColumnStepEnabled: boolean;
  openFieldMenu: () => void;
};

type ChangeboardingActions = {
  onDismiss: () => void;
  onPrimaryAction: () => void;
};

const ChangeboardingActionsRegistration: FunctionComponent<{
  actions: ChangeboardingActions;
  onMount: (actionsRef: MutableRefObject<ChangeboardingActions>) => void;
  onUnmount: (actionsRef: MutableRefObject<ChangeboardingActions>) => void;
}> = ({ actions, onMount, onUnmount }) => {
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  useLayoutEffect(() => {
    onMount(actionsRef);
    return () => onUnmount(actionsRef);
  }, [onMount, onUnmount]);

  return null;
};

export const FieldMenuSpotlightTourProvider: FunctionComponent<
  FieldMenuSpotlightTourProviderProps
> = ({
  children,
  closeFieldMenu,
  databaseViewportAnchorRef,
  fieldMenuOpenEvent,
  isEnabled,
  isFreezeColumnStepEnabled,
  openFieldMenu
}) => {
  const [activeStep, setActiveStep] = useState<FieldMenuSpotlightStep>('field-header');
  const [fieldHeaderTargetStep, setFieldHeaderTargetStep] =
    useState<FieldMenuSpotlightStep>('field-header');
  const [isExperienceActive, setIsExperienceActive] = useState(false);
  const changeboardingActionsRef = useRef<MutableRefObject<ChangeboardingActions> | null>(
    null
  );
  const latestFieldMenuOpenEventRef = useRef(fieldMenuOpenEvent);
  const previousFieldMenuOpenEventRef = useRef(fieldMenuOpenEvent);
  latestFieldMenuOpenEventRef.current = fieldMenuOpenEvent;
  const handleMount = useCallback(
    () => manuallyFireExposureEvent('databasesContextualMenusM2'),
    []
  );
  const registerActions = useCallback(
    (actionsRef: MutableRefObject<ChangeboardingActions>) => {
      changeboardingActionsRef.current = actionsRef;
      if (latestFieldMenuOpenEventRef.current?.source === 'drag-handle') {
        setFieldHeaderTargetStep('field-rename');
      }
      setIsExperienceActive(true);
    },
    []
  );
  const unregisterActions = useCallback(
    (actionsRef: MutableRefObject<ChangeboardingActions>) => {
      if (changeboardingActionsRef.current === actionsRef) {
        changeboardingActionsRef.current = null;
        setIsExperienceActive(false);
      }
    },
    []
  );

  useEffect(() => {
    if (previousFieldMenuOpenEventRef.current?.id === fieldMenuOpenEvent?.id) {
      return;
    }

    previousFieldMenuOpenEventRef.current = fieldMenuOpenEvent;

    if (!fieldMenuOpenEvent || !isExperienceActive || activeStep !== 'field-header') {
      return;
    }

    if (fieldMenuOpenEvent.source === 'drag-handle') {
      setFieldHeaderTargetStep('field-rename');
      return;
    }

    if (fieldMenuOpenEvent.source === 'field-header') {
      setActiveStep('field-rename');
    }
  }, [activeStep, fieldMenuOpenEvent, isExperienceActive]);

  if (!isEnabled) {
    return <>{children}</>;
  }

  const actions = changeboardingActionsRef.current?.current;
  const steps = isFreezeColumnStepEnabled
    ? FIELD_MENU_SPOTLIGHT_STEPS
    : FIELD_MENU_SPOTLIGHT_STEPS.slice(0, 2);
  const tourContext =
    isExperienceActive && actions
      ? {
          activeStep,
          activeTargetStep:
            activeStep === 'field-header' ? fieldHeaderTargetStep : activeStep,
          back: () => {
            if (activeStep === 'field-rename') {
              closeFieldMenu();
              setFieldHeaderTargetStep('field-header');
              setActiveStep('field-header');
              return;
            }
            setActiveStep('field-rename');
          },
          databaseViewportAnchorRef,
          dismiss: actions.onDismiss,
          next: () => {
            if (activeStep === 'field-header') {
              if (fieldHeaderTargetStep === 'field-header') {
                openFieldMenu();
              }
              setActiveStep('field-rename');
              return;
            }
            if (isFreezeColumnStepEnabled) {
              setActiveStep('freeze-column');
            }
          },
          done: actions.onPrimaryAction,
          steps
        }
      : null;

  return (
    <>
      <FieldMenuSpotlightTourContext.Provider value={tourContext}>
        {children}
      </FieldMenuSpotlightTourContext.Provider>
      {typeof document === 'undefined'
        ? null
        : createPortal(
            <ChangeboardingExperience
              feature="dbContextualMenusM2FieldMenuTour"
              onMount={handleMount}
            >
              {(changeboardingActions) => (
                <ChangeboardingActionsRegistration
                  actions={changeboardingActions}
                  onMount={registerActions}
                  onUnmount={unregisterActions}
                />
              )}
            </ChangeboardingExperience>,
            document.body
          )}
    </>
  );
};

const stepMessage = {
  'field-header': messages['contextual-menus-m2-tour.field-header'],
  'field-rename': messages['contextual-menus-m2-tour.field-rename'],
  'freeze-column': messages['contextual-menus-m2-tour.freeze-column']
} as const;

const stepHeadline = {
  'field-header': messages['contextual-menus-m2-tour.field-header.title'],
  'field-rename': messages['contextual-menus-m2-tour.field-rename.title'],
  'freeze-column': messages['contextual-menus-m2-tour.freeze-column.title']
} as const;

const stepPrimaryAction = {
  'field-header': messages['onboarding-tour.advance-spotlight'],
  'field-rename': messages['onboarding-tour.advance-spotlight'],
  'freeze-column': messages['onboarding-tour.complete-spotlight']
} as const;

type FieldMenuSpotlightTargetProps = {
  alignToContextualMenu?: boolean;
  children?: ReactNode;
  offset?: [number, number];
  placement: Placement;
  step: FieldMenuSpotlightStep;
  targetRef?: RefObject<HTMLElement | null>;
  waitForTargetLayout?: boolean;
};

type PopperReferenceElement = {
  contextElement: HTMLElement;
  getBoundingClientRect: () => DOMRect;
};

const getTargetRect = (target: HTMLElement, alignToContextualMenu: boolean): DOMRect => {
  if (!alignToContextualMenu) {
    return target.getBoundingClientRect();
  }

  const menu = target.closest<HTMLElement>('[data-contextual-menu="true"]');
  if (!menu) {
    return target.getBoundingClientRect();
  }

  const menuRect = menu.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return new DOMRect(menuRect.left, targetRect.top, menuRect.width, targetRect.height);
};

const getViewportAwarePlacement = (
  placement: Placement,
  targetRect: DOMRect,
  distance: number,
  databaseViewportAnchorRef?: RefObject<HTMLElement | null>
): Placement => {
  const databaseViewport = databaseViewportAnchorRef?.current?.closest<HTMLElement>(
    DATABASE_SCROLL_CONTAINER_SELECTOR
  );
  const databaseViewportRect = databaseViewport?.getBoundingClientRect();
  const boundaryLeft = Math.max(0, databaseViewportRect?.left ?? 0);
  const boundaryRight = Math.min(
    window.innerWidth,
    databaseViewportRect?.right ?? window.innerWidth
  );
  const requiredSpace =
    SPOTLIGHT_CARD_WIDTH + SPOTLIGHT_CARD_SIDE_OFFSET + distance + VIEWPORT_PADDING;
  const spaceOnLeft = targetRect.left - boundaryLeft;
  const spaceOnRight = boundaryRight - targetRect.right;

  if (
    (placement === 'right-start' || placement === 'right-end') &&
    spaceOnRight < requiredSpace &&
    spaceOnLeft > spaceOnRight
  ) {
    return placement === 'right-start' ? 'left-start' : 'left-end';
  }

  if (
    (placement === 'left-start' || placement === 'left-end') &&
    spaceOnLeft < requiredSpace &&
    spaceOnRight > spaceOnLeft
  ) {
    return placement === 'left-start' ? 'right-start' : 'right-end';
  }

  return placement;
};

const ExternalPopoverTarget: FunctionComponent<{
  alignToContextualMenu: boolean;
  targetRef: RefObject<HTMLElement | null>;
}> = ({ alignToContextualMenu, targetRef }) => (
  <Reference>
    {({ ref }) => (
      <ExternalPopoverTargetRegistration
        alignToContextualMenu={alignToContextualMenu}
        setReference={
          ref as unknown as (element: HTMLElement | PopperReferenceElement | null) => void
        }
        targetRef={targetRef}
      />
    )}
  </Reference>
);

const ExternalPopoverTargetRegistration: FunctionComponent<{
  alignToContextualMenu: boolean;
  setReference: (element: HTMLElement | PopperReferenceElement | null) => void;
  targetRef: RefObject<HTMLElement | null>;
}> = ({ alignToContextualMenu, setReference, targetRef }) => {
  useLayoutEffect(() => {
    const target = targetRef.current;
    if (!target) {
      setReference(null);
      return;
    }

    if (!alignToContextualMenu) {
      setReference(target);
      return () => setReference(null);
    }

    let animationFrame: number | undefined;
    const registerVirtualReference = () => {
      setReference({
        contextElement: target,
        getBoundingClientRect: () => getTargetRect(target, true)
      });
    };
    const updateVirtualReference = () => {
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(registerVirtualReference);
    };

    registerVirtualReference();
    window.addEventListener('resize', updateVirtualReference);
    window.addEventListener('scroll', updateVirtualReference, true);
    return () => {
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('resize', updateVirtualReference);
      window.removeEventListener('scroll', updateVirtualReference, true);
      setReference(null);
    };
  }, [alignToContextualMenu, setReference, targetRef]);

  return null;
};

const SpotlightInteractionBoundary: FunctionComponent<{ children: ReactNode }> = ({
  children
}) => (
  <div
    role="presentation"
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
  >
    {children}
  </div>
);

export const FieldMenuSpotlightTarget: FunctionComponent<
  FieldMenuSpotlightTargetProps
> = ({
  alignToContextualMenu = false,
  children,
  offset = [0, 8],
  placement,
  step,
  targetRef,
  waitForTargetLayout = false
}) => {
  const tour = useContext(FieldMenuSpotlightTourContext);
  const { formatMessage } = useIntl();
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);
  const [isTargetLayoutReady, setIsTargetLayoutReady] = useState(!waitForTargetLayout);
  const isActive = tour?.activeTargetStep === step;
  const distance = offset[1];
  const databaseViewportAnchorRef = tour?.databaseViewportAnchorRef;

  useLayoutEffect(() => {
    if (!isActive || !waitForTargetLayout) {
      setIsTargetLayoutReady(true);
      return;
    }

    let isCancelled = false;
    setIsTargetLayoutReady(false);
    afterNextLayout(() => {
      if (!isCancelled && targetRef?.current) {
        setIsTargetLayoutReady(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [isActive, targetRef, waitForTargetLayout]);

  useLayoutEffect(() => {
    if (!isActive || !targetRef?.current) {
      setResolvedPlacement(placement);
      return;
    }

    let animationFrame: number | undefined;
    const updatePlacement = () => {
      if (targetRef.current) {
        setResolvedPlacement(
          getViewportAwarePlacement(
            placement,
            getTargetRect(targetRef.current, alignToContextualMenu),
            distance,
            databaseViewportAnchorRef
          )
        );
      }
    };
    const schedulePlacementUpdate = () => {
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(updatePlacement);
    };

    updatePlacement();
    window.addEventListener('resize', schedulePlacementUpdate);
    window.addEventListener('scroll', schedulePlacementUpdate, true);
    return () => {
      if (animationFrame !== undefined) {
        cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('resize', schedulePlacementUpdate);
      window.removeEventListener('scroll', schedulePlacementUpdate, true);
    };
  }, [
    alignToContextualMenu,
    databaseViewportAnchorRef,
    distance,
    isActive,
    placement,
    targetRef
  ]);

  if (!tour || !isActive || !isTargetLayoutReady) {
    return <>{children}</>;
  }

  const spotlightStep = tour.activeStep;
  const stepIndex = tour.steps.indexOf(spotlightStep);
  const canGoBack = stepIndex > 0;
  const isLastStep = stepIndex === tour.steps.length - 1;

  const spotlightContent = (
    <SpotlightInteractionBoundary>
      <PopoverContent
        placement={resolvedPlacement}
        offset={offset}
        strategy="fixed"
        dismiss={ignorePopoverDismiss}
        shouldDismissOnClickOutside={false}
        {...(canGoBack ? { back: tour.back } : {})}
        {...(isLastStep ? { done: tour.done } : { next: tour.next })}
        testId={`field-menu-spotlight-${spotlightStep}`}
      >
        <SpotlightCard>
          <SpotlightHeader>
            <SpotlightHeadline>
              <Heading color="color.text.inverse" size="xsmall">
                {formatMessage(stepHeadline[spotlightStep])}
              </Heading>
            </SpotlightHeadline>
            <SpotlightControls>
              <SpotlightDismissControl onClick={tour.dismiss} />
            </SpotlightControls>
          </SpotlightHeader>
          <Box paddingBlockEnd="space.100">
            <SpotlightBody>{formatMessage(stepMessage[spotlightStep])}</SpotlightBody>
          </Box>
          <SpotlightFooter>
            <SpotlightStepCount>
              {formatMessage(messages['contextual-menus-m2-tour.step-count'], {
                currentStep: stepIndex + 1,
                totalSteps: tour.steps.length
              })}
            </SpotlightStepCount>
            <SpotlightActions>
              {canGoBack && (
                <SpotlightSecondaryAction>
                  {formatMessage(messages['contextual-menus-m2-tour.back'])}
                </SpotlightSecondaryAction>
              )}
              <Button
                appearance="primary"
                onClick={isLastStep ? tour.done : tour.next}
                spacing="compact"
                testId="field-menu-spotlight-primary-action"
              >
                {formatMessage(
                  isLastStep
                    ? messages['onboarding-tour.complete-spotlight']
                    : stepPrimaryAction[spotlightStep]
                )}
              </Button>
            </SpotlightActions>
          </SpotlightFooter>
        </SpotlightCard>
      </PopoverContent>
    </SpotlightInteractionBoundary>
  );

  return (
    <PopoverProvider>
      {targetRef ? (
        <>
          {children}
          <ExternalPopoverTarget
            alignToContextualMenu={alignToContextualMenu}
            targetRef={targetRef}
          />
        </>
      ) : (
        <PopoverTarget>{children}</PopoverTarget>
      )}
      {createPortal(spotlightContent, document.body)}
    </PopoverProvider>
  );
};
